import { memo } from 'react';
import { Box, makeStyles, Tooltip } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import clsx from 'clsx';
import { NumericFormat } from 'react-number-format';
import { TextColor } from '@setvi/shared/enums';
import { ProfitMarginPercentageInput } from 'pages/quotes/components/quote-items/components/profit-margin-percentage-input';
import type { QuoteProductDTO } from '@setvi/shared/services/react-query';
import { FirstRowBox, RowContainer, SecondRowBox } from '../styled';
import {
  NO_FIELD_LOCK,
  type FieldLockState
} from '../../../../pages/details/realtime/use-field-locks';
import { FieldLockIcon, FieldLockAvatar } from '../field-lock-badge';

interface GpColumnProps {
  product: QuoteProductDTO;
  productId: string;
  onProfitMarginChange: (
    productId: string,
    product: QuoteProductDTO,
    value: string
  ) => void;
  onProfitMarginPercentageChange: (
    productId: string,
    product: QuoteProductDTO,
    value: string
  ) => void;
  // Each GP input's own lock — drives the editor avatar shown ON that input
  // (only when THAT field is the one being edited).
  gpLock?: FieldLockState;
  gpPercentLock?: FieldLockState;
  // True when any pricing input (Sell / GP$ / GP%) is held by another user: the
  // three lock+disable together, but the avatar only shows on the held one.
  // All omitted by non-collab consumers (Auto RFQ quote tab) → no locking.
  pricingLockedByOther?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  alwaysEditable?: boolean;
}

const useStyles = makeStyles(() => ({
  input: {
    width: '110px',
    textAlign: 'center',
    borderRadius: '99px !important',
    border: '1px solid #e2e2ea',
    height: '40px',
    padding: '10px',
    fontSize: '16px',
    backgroundColor: '#F8F9FA',
    '& input[type=number]': {
      MozAppearance: 'textfield',
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        display: 'none'
      }
    }
  },
  // "Locked by another user" state: soft blue accent instead of dead grey.
  locked: {
    backgroundColor: '#EEF4FF !important',
    borderColor: '#9DC7F2 !important',
    color: '#5D7083 !important',
    WebkitTextFillColor: '#5D7083 !important',
    cursor: 'not-allowed !important'
  },
  // Extra left room for the in-input lock glyph — only on the input that's
  // actually being edited (shows the glyph), so grouped-but-not-edited pricing
  // inputs don't get an empty gap.
  glyphPad: {
    paddingLeft: '32px !important'
  }
}));

export const GpColumn = memo(
  ({
    product,
    productId,
    onProfitMarginChange,
    onProfitMarginPercentageChange,
    gpLock = NO_FIELD_LOCK,
    gpPercentLock = NO_FIELD_LOCK,
    pricingLockedByOther = false,
    disabled = false,
    disabledTooltip = '',
    alwaysEditable = false
  }: GpColumnProps) => {
    const classes = useStyles();

    if (product?.isPricingFetching) {
      return (
        <RowContainer>
          <FirstRowBox>
            <Box display="flex" gridGap="15px">
              <Skeleton variant="rect" width={145} height={40} />
              <Skeleton variant="rect" width={110} height={40} />
            </Box>
          </FirstRowBox>
          <SecondRowBox />
        </RowContainer>
      );
    }

    const isInputDisabled =
      !alwaysEditable &&
      (disabled || (!product?.isEditable && !product?.isFreeHand));

    const gpInputs = (
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '15px'
        }}>
        <Box style={{ position: 'relative', display: 'inline-flex' }}>
          <NumericFormat
            value={product?.totalGrossProfit}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator=","
            decimalSeparator="."
            prefix="$ "
            onChange={e => {
              onProfitMarginChange(
                productId,
                product,
                e.target.value.replace(/[^\d.-]/g, '')
              );
            }}
            onFocus={gpLock.acquire}
            onBlur={gpLock.release}
            disabled={isInputDisabled || pricingLockedByOther}
            className={clsx(
              classes.input,
              pricingLockedByOther && classes.locked,
              gpLock.lockedByOther && classes.glyphPad
            )}
            style={{
              width: 145,
              fontWeight: 600,
              color:
                product?.totalGrossProfitPercentage > 0
                  ? TextColor.Green
                  : TextColor.Red
            }}
          />
          {gpLock.lockedByOther && (
            <>
              <FieldLockIcon />
              <FieldLockAvatar holder={gpLock.holder} />
            </>
          )}
        </Box>
        <Box style={{ position: 'relative', display: 'inline-flex' }}>
          <ProfitMarginPercentageInput
            value={product?.totalGrossProfitPercentage}
            productId={productId}
            product={product}
            isEditable={
              (alwaysEditable ||
                (!disabled && (product?.isEditable || product?.isFreeHand))) &&
              !pricingLockedByOther
            }
            maxValue={
              product?.maxGpPercentage
                ? Number(product?.maxGpPercentage)
                : 99.99
            }
            minValue={
              product?.minGpPercentage ? Number(product?.minGpPercentage) : 0
            }
            onProfitMarginPercentageChange={onProfitMarginPercentageChange}
            onFocusChange={focused =>
              focused ? gpPercentLock.acquire() : gpPercentLock.release()
            }
            inputClassName={clsx(
              classes.input,
              pricingLockedByOther && classes.locked,
              gpPercentLock.lockedByOther && classes.glyphPad
            )}
            textWithColors
          />
          {gpPercentLock.lockedByOther && (
            <>
              <FieldLockIcon />
              <FieldLockAvatar holder={gpPercentLock.holder} />
            </>
          )}
        </Box>
      </Box>
    );

    return (
      <RowContainer>
        <FirstRowBox>
          {disabled && disabledTooltip ? (
            <Tooltip title={disabledTooltip} arrow>
              <Box>{gpInputs}</Box>
            </Tooltip>
          ) : (
            gpInputs
          )}
        </FirstRowBox>
        <SecondRowBox />
      </RowContainer>
    );
  }
);

GpColumn.displayName = 'GpColumn';
